/* 这里是 FreeCanvas 的接口封装，统一放当前画布页直接调用的请求方法。 */
import { message } from 'antd';
import API, { http } from '@api/api';
import Utils from '@utils/utils';

const DEFAULT_PROJECT_NAME = '未命名项目';
const EXECUTION_QUERY_TIMEOUT_MS = 20000;

const cloneValue = (value) => {
    if (typeof structuredClone === 'function') return structuredClone(value);
    return JSON.parse(JSON.stringify(value));
};

const ensureApiSuccess = (result, fallbackMessage) => {
    if (result?.code === 0 || result?.code === 200) return;
    const error = new Error(result?.message || fallbackMessage);
    error.code = result?.code;
    error.data = result?.data;
    error.attr = result?.attr;
    throw error;
};

const getDuplicatedProjectName = (projectName = '') => {
    const nextProjectName = String(projectName || '').trim() || DEFAULT_PROJECT_NAME;
    return `${nextProjectName}副本`;
};

const sanitizeDuplicatedProjectNode = (node) => {
    const nextNode = cloneValue(node || {});
    const renderData = nextNode?.renderData && typeof nextNode.renderData === 'object'
        ? nextNode.renderData
        : {};
    const renderNodeData = renderData?.data && typeof renderData.data === 'object'
        ? renderData.data
        : {};
    const nextNodeType = renderData?.type || nextNode?.nodeType || '';
    const nextRenderNodeData = {
        ...renderNodeData,
        latestExecution: null,
        executionStatus: 'idle',
        selectedOutputId: '',
        activeExecutionId: '',
        groupExecutionLocked: false,
    };

    if (nextNodeType === 'functionNode' || nextNodeType === 'function') {
        nextRenderNodeData.displayMode = 'empty';
    }

    return {
        ...nextNode,
        latestExecution: null,
        renderData: {
            ...renderData,
            data: nextRenderNodeData,
        },
    };
};

const prepareFlowImageFile = async (file) => {
    if (!file) return null;

    const isValid = await Utils.validateImageFile(file);
    if (!isValid) return null;

    const resizeResult = await Utils.resizeImageFileMaxByAliyunBy(file, 4096);
    if (!resizeResult?.success || !resizeResult?.file) {
        message.warning(resizeResult?.message || '图片处理失败，请更换图片后重试');
        return null;
    }

    return resizeResult.file;
};

// 创建一个新的画布项目。
export const createFlowProject = async () => {
    const project = await API.commonApiInvoker(
        API.saveFlowProject({
            id: '',
            projectName: '',
            cover: '',
        }),
        false,
        true,
    );

    return {
        id: String(project?.id ?? project?.projectId ?? `flow-project-${Date.now()}`),
        name: project?.projectName || project?.name || DEFAULT_PROJECT_NAME,
        cover: project?.cover || '',
        studioId: project?.studioId || '',
        version: Number(project?.version ?? 0),
    };
};

// 保存项目基础信息，比如项目名称和封面。
export const saveFlowProject = async ({
    id,
    projectName,
    cover = '',
    successMessage = '项目已保存',
} = {}) => {
    const project = await API.commonApiInvoker(
        API.saveFlowProject({
            id,
            projectName,
            cover,
        }),
        false,
        true,
    );

    if (successMessage) {
        message.success(successMessage);
    }

    return {
        id: String(project?.id ?? id ?? ''),
        name: project?.projectName || project?.name || projectName || DEFAULT_PROJECT_NAME,
        cover: project?.cover || cover,
        studioId: project?.studioId || '',
        version: Number(project?.version ?? 0),
    };
};

// 删除指定的画布项目。
export const deleteFlowProject = async (projectIds) => {
    const nextProjectIds = Array.isArray(projectIds)
        ? projectIds.filter(Boolean)
        : [projectIds].filter(Boolean);

    if (nextProjectIds.length === 0) return true;

    await API.commonApiInvoker(
        API.deleteFlowProject({
            projectIds: nextProjectIds,
        }),
        false,
        true,
    );

    message.success('项目已删除');

    return true;
};

// 置顶指定的画布项目。
export const pinFlowProject = async (projectId) => {
    await API.commonApiInvoker(
        API.pinFlowProject({
            projectId,
        }),
        false,
        true,
    );

    return true;
};

// 拖拽调整项目顺序。
export const sortFlowProject = async ({
    id,
    afterProjectId,
} = {}) => {
    await API.commonApiInvoker(
        API.sortFlowProject({
            id,
            afterProjectId,
        }),
        false,
        true,
    );

    return true;
};

// 创建画布副本，仅复制输入、配置和结构，不复制生成结果。
export const duplicateFlowProject = async ({
    projectId,
    projectName = '',
    cover = '',
    successMessage = '副本已创建',
} = {}) => {
    const sourceProjectDetail = await getFlowProjectDetail(projectId);
    const createdProject = await createFlowProject();
    let nextProject = await saveFlowProject({
        id: createdProject.id,
        projectName: getDuplicatedProjectName(sourceProjectDetail?.name || projectName),
        cover: '',
        successMessage: '',
    });
    const nodeOps = (sourceProjectDetail?.nodes || []).map((node) => ({
        op: 'add',
        node: sanitizeDuplicatedProjectNode(node),
    }));
    const edgeOps = (sourceProjectDetail?.edges || []).map((edge) => ({
        op: 'add',
        edge: cloneValue(edge),
    }));

    if (nodeOps.length > 0 || edgeOps.length > 0) {
        const duplicatedCanvas = await batchUpdateFlowCanvas({
            projectId: nextProject.id,
            version: Number(nextProject.version ?? 0),
            nodeOps,
            edgeOps,
        });

        nextProject = {
            ...nextProject,
            cover: '',
            version: Number(
                duplicatedCanvas?.version
                ?? duplicatedCanvas?.canvasVersion
                ?? duplicatedCanvas?.projectVersion
                ?? nextProject.version
                ?? 0,
            ),
        };
    }

    if (successMessage) {
        message.success(successMessage);
    }

    return {
        ...nextProject,
        cover: '',
        sourceProjectDetail,
        sourceCover: cover,
    };
};

// 保存工作流模板。
export const saveFlowTemplate = async ({
    projectId,
    groupNodeId,
    templateName,
    cover = '',
    remark = '',
    toPublic = false,
} = {}) => {
    await API.commonApiInvoker(
        API.saveFlowTemplate({
            projectId,
            groupNodeId,
            templateName,
            cover,
            remark,
            toPublic,
        }),
        false,
        true,
    );

    message.success('模板已保存');

    return true;
};

// 上传画布里使用的图片，并返回最终图片地址。
export const uploadFlowImage = async (file) => {
    if (!file) return '';

    const preparedFile = await prepareFlowImageFile(file);
    if (!preparedFile) return '';

    const uploadedUrl = await Utils.uploadFile(preparedFile);

    return typeof uploadedUrl === 'string' ? uploadedUrl : '';
};

// 分页查询侧边栏项目列表。
export const getFlowProjectPage = async ({
    keyword = '',
    order = '',
    sortField = '',
    pageIndex = 1,
    pageSize = 20,
    member,
} = {}) => {
    const pageData = await API.commonApiInvoker(
        API.getFlowProjectPage({
            keyword: keyword || undefined,
            order: order || undefined,
            sortField: sortField || undefined,
            pageIndex,
            pageSize,
            member: member === undefined ? undefined : member,
        }),
        false,
        true,
    );

    const items = Array.isArray(pageData?.items) ? pageData.items : [];

    return {
        pageIndex: pageData?.pageIndex ?? pageIndex,
        pageSize: pageData?.pageSize ?? pageSize,
        total: pageData?.total ?? items.length,
        totalPages: pageData?.totalPages ?? 0,
        items: items.map((item) => ({
            id: String(item?.id ?? ''),
            name: item?.projectName || DEFAULT_PROJECT_NAME,
            projectName: item?.projectName || DEFAULT_PROJECT_NAME,
            userId: item?.userId || '',
            memberName: item?.memberName || '',
            flowSerial: item?.flowSerial ?? '',
            cover: item?.cover || '',
            nodes: Array.isArray(item?.nodes) ? item.nodes : [],
            edges: Array.isArray(item?.edges) ? item.edges : [],
            studioId: item?.studioId || '',
            pinned: !!item?.pinned,
            version: Number(item?.version ?? 0),
            updatedTime: item?.updatedTime || '',
        })),
    };
};

// 分页查询模板库。
export const getFlowTemplatePage = async ({
    scope = '',
    keyword = '',
    createdBy = '',
    studioId = '',
    order = '',
    sortField = '',
    admin,
    pageIndex = 1,
    pageSize = 12,
} = {}) => {
    const pageData = await API.commonApiInvoker(
        API.getFlowTemplatePage({
            ...(scope ? { scope } : {}),
            ...(keyword ? { keyword } : {}),
            ...(createdBy ? { createdBy } : {}),
            ...(studioId ? { studioId } : {}),
            ...(order ? { order } : {}),
            ...(sortField ? { sortField } : {}),
            ...(typeof admin === 'boolean' ? { admin } : {}),
            pageIndex,
            pageSize,
        }),
        false,
        true,
    );

    const items = Array.isArray(pageData?.items) ? pageData.items : [];

    return {
        pageIndex: pageData?.pageIndex ?? pageIndex,
        pageSize: pageData?.pageSize ?? pageSize,
        total: pageData?.total ?? items.length,
        totalPages: pageData?.totalPages ?? 0,
        items: items.map((item) => ({
            id: String(item?.id ?? ''),
            templateName: item?.templateName || '未命名模板',
            remark: item?.remark || '',
            cover: item?.cover || '',
            favorite: !!item?.favorite,
            canDelete: !!item?.canDelete,
            canPublish: !!item?.canPublish,
            studioId: item?.studioId || '',
            createdBy: item?.createdBy || '',
            updatedBy: item?.updatedBy || '',
            creatorName: item?.creatorName || '',
            studioName: item?.studioName || '',
            createdTime: item?.createdTime || '',
            updatedTime: item?.updatedTime || '',
        })),
    };
};

// 查询模板创建人筛选项。
export const getFlowTemplateCreators = async () => {
    const result = await API.getFlowTemplateCreators();
    ensureApiSuccess(result, '加载模板创建人失败');

    return Array.isArray(result?.data) ? result.data : [];
};

// 查询模板所属工作室筛选项；当前宿主项目未接入该接口时兜底为空数组。
export const getFlowTemplateStudios = async () => {
    if (typeof API.getAllMerchantStudios !== 'function') {
        return [];
    }

    const studios = await API.commonApiInvoker(
        API.getAllMerchantStudios(),
        false,
        true,
    );

    return Array.isArray(studios) ? studios : [];
};

// 查询单个模板详情。
export const getFlowTemplateDetail = async (templateId) => {
    const result = await API.getFlowTemplateDetail({
        templateId,
    });
    ensureApiSuccess(result, '加载模板详情失败');

    return result?.data || {};
};

// 查询单个项目详情，并返回画布节点和边数据。
export const getFlowProjectDetail = async (projectId) => {
    const result = await API.getFlowProjectDetail({
        projectId,
    });
    ensureApiSuccess(result, '加载项目详情失败');

    const project = result?.data || {};

    return {
        id: String(project?.id ?? projectId ?? ''),
        name: project?.projectName || DEFAULT_PROJECT_NAME,
        flowSerial: project?.flowSerial || '',
        cover: project?.cover || '',
        studioId: project?.studioId || '',
        version: Number(project?.version ?? 0),
        nodes: Array.isArray(project?.nodes) ? project.nodes : [],
        edges: Array.isArray(project?.edges) ? project.edges : [],
    };
};

// 增量提交当前画布的节点和边变更。
export const batchUpdateFlowCanvas = async (payload) => {
    const { studioId, ...nextPayload } = payload || {};
    const result = await API.batchUpdateFlowCanvas(nextPayload);
    ensureApiSuccess(result, '更新画布失败');

    return result?.data ?? result ?? {};
};

// 触发单个节点执行生成。
export const executeFlowNode = async ({ projectId, nodeId }) => {
    const result = await API.executeFlowNode({
        projectId,
        nodeId,
    });
    console.log('[FreeCanvas][executeFlowNode] raw API result:', result);
    ensureApiSuccess(result, '执行节点失败');

    return result?.data ?? {};
};

// 触发整组节点执行生成。
export const executeFlowGroup = async ({ projectId, groupNodeId, manual }) => {
    const result = await API.executeFlowGroup({
        projectId,
        groupNodeId,
        manual,
    });
    ensureApiSuccess(result, '执行分组失败');

    return result?.data ?? {};
};

// 恢复人工选图后暂停的执行流。
export const resumeFlowExecution = async ({
    executionId,
    nodeId,
    outputId,
} = {}) => {
    const result = await API.resumeFlowExecution({
        executionId,
        nodeId,
        outputId,
    });
    ensureApiSuccess(result, '恢复执行失败');

    return result?.data ?? {};
};

// 批量查询执行进度和结果。
export const getFlowExecutionBatch = async (executionIds, signal) => {
    console.log('[FreeCanvas][getFlowExecutionBatch] requesting, executionIds:', executionIds);
    const response = await http.get('/ps/web/flow/execution/batch-get', {
        params: {
            executionIds,
            ts: new Date().getTime(),
        },
        signal,
        timeout: EXECUTION_QUERY_TIMEOUT_MS,
    });
    const result = response?.data;
    console.log('[FreeCanvas][getFlowExecutionBatch] response result:', result);
    ensureApiSuccess(result, '查询执行进度失败');

    return result?.data ?? {};
};

// 选择节点当前主图输出，用于保存结果选图状态。
export const selectFlowExecutionPrimaryOutput = async ({ nodeExecutionId, outputId }) => {
    const result = await API.selectFlowExecutionPrimaryOutput({
        nodeExecutionId,
        outputId,
    });
    ensureApiSuccess(result, '选择主图失败');

    return result?.data ?? {};
};

// 获取单张输出图的接口下载地址。
export const downloadFlowOutputImage = async (projectId, outputId) => {
    const urls = await downloadFlowOutputImages(projectId, [outputId]);
    return urls[0] || '';
};

// 获取多张输出图的接口下载地址。
export const downloadFlowOutputImages = async (projectId, outputIds = []) => {
    const normalizedOutputIds = Array.isArray(outputIds)
        ? outputIds.filter(Boolean)
        : [outputIds].filter(Boolean);

    if (!projectId || normalizedOutputIds.length === 0) return [];

    const result = await API.downloadFlowOutputImages({
        projectId,
        outputIds: normalizedOutputIds,
    });
    ensureApiSuccess(result, '获取下载链接失败');

    return Array.isArray(result?.data) ? result.data : [];
};

// 查询当前项目的历史生成图片记录。
export const getFlowProjectHistoryImages = async ({
    projectId,
    pageIndex = 1,
    pageSize = 50,
} = {}) => {
    const result = await API.getFlowProjectHistoryImages({
        projectId,
        pageIndex,
        pageSize,
    });
    ensureApiSuccess(result, '加载生成记录失败');

    const pageData = result?.data || {};
    const items = Array.isArray(pageData?.items) ? pageData.items : [];

    return {
        pageIndex: Number(pageData?.pageIndex ?? pageIndex),
        pageSize: Number(pageData?.pageSize ?? pageSize),
        totalPages: Number(pageData?.totalPages ?? 0),
        total: Number(pageData?.total ?? items.length),
        items,
    };
};

// 批量查询多个项目的历史生成图片记录。
export const getFlowProjectHistoryImagesBatch = async ({
    projectIds = [],
} = {}) => {
    const nextProjectIds = Array.isArray(projectIds)
        ? projectIds.filter(Boolean)
        : [projectIds].filter(Boolean);

    if (nextProjectIds.length === 0) return [];

    const result = await API.getFlowProjectHistoryImagesBatch({
        projectIds: nextProjectIds,
    });
    ensureApiSuccess(result, '加载批量下载图片失败');

    return Array.isArray(result?.data) ? result.data : [];
};

// 收藏或取消收藏模板。
export const favoriteFlowTemplate = async (templateId) => {
    const result = await API.favoriteFlowTemplate({
        templateId,
    });
    ensureApiSuccess(result, '收藏模板失败');

    return result?.data ?? {};
};

// 发布模板到公共模板库。
export const publishFlowTemplate = async (templateId) => {
    const result = await API.publishFlowTemplate({
        templateId,
    });
    ensureApiSuccess(result, '发布模板失败');

    return result?.data ?? {};
};

// 删除模板。
export const deleteFlowTemplate = async ({
    templateId,
    cascade = false,
} = {}) => {
    const result = await API.deleteFlowTemplate({
        templateId,
        cascade,
    });
    ensureApiSuccess(result, '删除模板失败');

    return result?.data ?? {};
};

// 把模板 JSON（nodes/edges）重新随机 id 后批量写入指定项目。
// 返回 { nodes, edges } 为重新赋 id 后的服务端格式，调用方可用于本地状态恢复。
export const applyTemplateToCanvas = async ({ projectId, version = 0, templateNodes, templateEdges }) => {
    const createId = (prefix) => `${prefix}-${Date.now()}${Math.random().toString(16).slice(2)}`;
    const remapPanelState = (panelState = {}) => {
        if (!panelState || typeof panelState !== 'object') {
            return panelState;
        }

        const nextPanelState = { ...panelState };

        if (Array.isArray(panelState.linkedUploads)) {
            nextPanelState.linkedUploads = panelState.linkedUploads.map((item) => ({
                ...item,
                edgeId: edgeIdMap.get(item?.edgeId) || item?.edgeId,
                sourceNodeId: nodeIdMap.get(item?.sourceNodeId) || item?.sourceNodeId,
            }));
        }

        if (
            panelState.linkedSlotSources
            && typeof panelState.linkedSlotSources === 'object'
            && !Array.isArray(panelState.linkedSlotSources)
        ) {
            nextPanelState.linkedSlotSources = Object.fromEntries(
                Object.entries(panelState.linkedSlotSources).map(([slotKey, value]) => [
                    slotKey,
                    value && typeof value === 'object'
                        ? {
                            ...value,
                            edgeId: edgeIdMap.get(value.edgeId) || value.edgeId,
                            sourceNodeId: nodeIdMap.get(value.sourceNodeId) || value.sourceNodeId,
                        }
                        : value,
                ]),
            );
        }

        return nextPanelState;
    };

    // 建立旧 id → 新 id 映射
    const nodeIdMap = new Map();
    (templateNodes || []).forEach((node) => {
        const prefix = node.id.startsWith('group-node') ? 'group-node'
            : node.id.startsWith('func-') ? 'func'
            : 'image-node';
        nodeIdMap.set(node.id, createId(prefix));
    });

    const edgeIdMap = new Map();
    (templateEdges || []).forEach((edge) => {
        edgeIdMap.set(edge.id, createId('edge'));
    });

    // 重写节点 id / parentId
    const newNodes = (templateNodes || []).map((node) => ({
        ...node,
        id: nodeIdMap.get(node.id),
        parentId: node.parentId ? nodeIdMap.get(node.parentId) : undefined,
        renderData: {
            ...node.renderData,
            parentId: node.renderData?.parentId ? nodeIdMap.get(node.renderData.parentId) : undefined,
            data: node.renderData?.data
                ? {
                    ...node.renderData.data,
                    panelState: remapPanelState(node.renderData.data.panelState),
                }
                : undefined,
        },
        paramBindings: (node.paramBindings || []).map((binding) => ({
            ...binding,
            sourceNodeId: nodeIdMap.get(binding.sourceNodeId) || binding.sourceNodeId,
        })),
    }));

    // 重写边 id / sourceNodeId / targetNodeId
    const newEdges = (templateEdges || []).map((edge) => ({
        ...edge,
        id: edgeIdMap.get(edge.id),
        sourceNodeId: nodeIdMap.get(edge.sourceNodeId) || edge.sourceNodeId,
        targetNodeId: nodeIdMap.get(edge.targetNodeId) || edge.targetNodeId,
    }));

    const nodeOps = newNodes.map((node) => ({ op: 'add', node }));
    const edgeOps = newEdges.map((edge) => ({ op: 'add', edge }));

    await batchUpdateFlowCanvas({ projectId, version, nodeOps, edgeOps });

    return { nodes: newNodes, edges: newEdges };
};
